import React, { useState } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, MapPin, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Hero = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    services: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare data for Formspree
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('services', formData.services.join(', '));
      formDataToSend.append('message', formData.message || 'Quote request');
      
      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/xpqodojk', {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Quote request submitted successfully! We\'ll contact you soon.');
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          services: [],
          message: ''
        });
      } else {
        const data = await response.json();
        if (data.errors) {
          toast.error(data.errors.map(error => error.message).join(', '));
        } else {
          toast.error('Failed to submit quote. Please try again.');
        }
      }
    } catch (error) {
      toast.error('Failed to submit quote. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black pt-36">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1541348263662-e068662d82af)',
          filter: 'brightness(0.3)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Content */}
        <div className="space-y-8 animate-fade-in">
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
            Detail Like a <span className="text-white">PRO</span>,<br />
            Ride Like a <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">BOSS!</span>
          </h1>
          
          <p className="text-xl text-gray-300 leading-relaxed">
            Premium mobile car detailing in Sacramento & surrounding areas! We bring expert interior & exterior detailing, waxing, and ceramic coatings straight to your location—convenience, quality, and a showroom shine in one package!
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: CheckCircle2, text: 'We Come to You' },
              { icon: CheckCircle2, text: 'Premium Protection' },
              { icon: CheckCircle2, text: 'Detailing Excellence' },
              { icon: CheckCircle2, text: '5-Star Rated' }
            ].map((feature, index) => (
              <div
                key={index}
                className="flex items-center space-x-2 text-white transform hover:translate-x-2 transition-transform duration-300"
              >
                <feature.icon className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <span className="font-medium">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <Button
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-6 text-lg rounded-md transform hover:scale-105 transition-all duration-300 shadow-2xl hover:shadow-orange-500/50"
              onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <MapPin className="w-5 h-5 mr-2" />
              Location
            </Button>
            <Button
              variant="outline"
              className="border-2 border-orange-500 text-white hover:bg-orange-500 hover:text-white font-bold px-8 py-6 text-lg rounded-md transform hover:scale-105 transition-all duration-300"
              onClick={() => window.location.href = 'tel:9162993189'}
            >
              <Phone className="w-5 h-5 mr-2" />
              (916) 299-3189
            </Button>
          </div>
        </div>

        {/* Right Content - Quote Form */}
        <div id="quote" className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl transform hover:scale-105 transition-all duration-300">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-center py-4 rounded-t-xl mb-6 -mx-8 -mt-8">
            <h3 className="text-2xl font-bold">Get Your Instant Quote!</h3>
          </div>
          <div className="bg-gradient-to-r from-orange-600/90 to-orange-700/90 text-white text-center py-3 rounded-lg mb-6">
            <p className="text-lg font-semibold flex items-center justify-center gap-2">
              <span className="text-2xl">⭐</span> 5★ Rated by Happy Customers!
            </p>
          </div>
          
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="text-white font-medium mb-2 block">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                placeholder="Your Name"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <label className="text-white font-medium mb-2 block">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                placeholder="Your Phone Number"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <label className="text-white font-medium mb-2 block">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                placeholder="Your Email"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <label className="text-white font-medium mb-2 block">Select All You are Interested In</label>
              <div className="space-y-2 max-h-48 overflow-y-auto bg-white/5 p-3 rounded-lg">
                {['Ceramic Coating', 'Paint Protection Film', 'Interior/Exterior Detailing', 'Aviation or Marine Detailing', 'Window Tinting'].map((service) => (
                  <label key={service} className="flex items-center space-x-2 text-white cursor-pointer hover:text-orange-500 transition-colors">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-orange-500"
                      checked={formData.services.includes(service)}
                      onChange={() => handleServiceToggle(service)}
                      disabled={isSubmitting}
                    />
                    <span>{service}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-white font-medium mb-2 block">Additional Message (Optional)</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all resize-none"
                placeholder="Tell us more about your needs..."
                rows="3"
                disabled={isSubmitting}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 text-lg rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-orange-500 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
